// ! UTILITY ENUMS TO ASSIST WITH DB
export const CrewClaimTypeEnum = {
    warranty: 'Warranty',
    return: 'Return',
};
// ! ensure that new columns are added to apps/api/src/services/crew/reports/ where the reports are generated
// the database's primary_entity: the records the app lists, searches, saves views of and exports
export const PrimaryEntity = {
    Return: 'Return',
    Warranty: 'Warranty',
    Shipping: 'Shipping',
    Registration: 'Registration',
    Order: 'Order',
};
// the primary entities with reports and data exports (orders have neither)
export const ReportableEntity = {
    Return: PrimaryEntity.Return,
    Warranty: PrimaryEntity.Warranty,
    Registration: PrimaryEntity.Registration,
    Shipping: PrimaryEntity.Shipping,
};
export const ClaimWindowStartEvent = {
    Order_Fulfilled_On: 'Order_Fulfilled_On',
    Order_Created_On: 'Order_Created_On',
    Order_Payment_Processed_On: 'Order_Payment_Processed_On',
    Order_Shipment_Delivered_On: 'Order_Shipment_Delivered_On',
};
export const claimWindowStartEventName = {
    [ClaimWindowStartEvent.Order_Created_On]: 'Order Created On',
    [ClaimWindowStartEvent.Order_Fulfilled_On]: 'Order Fulfilled On',
    [ClaimWindowStartEvent.Order_Shipment_Delivered_On]: 'Order Delivered On',
    [ClaimWindowStartEvent.Order_Payment_Processed_On]: 'Payment Processed On',
};
export const StoreInventoryBehavior = {
    Bypass: 'Bypass',
    Decrement_Ignoring_Policy: 'Decrement_Ignoring_Policy',
    Decrement_Obeying_Policy: 'Decrement_Obeying_Policy',
};
export const storeInventoryBehaviorName = {
    [StoreInventoryBehavior.Bypass]: 'Do Not Decrement Inventory',
    [StoreInventoryBehavior.Decrement_Ignoring_Policy]: 'Decrement Inventory (Ignore Policies)',
    [StoreInventoryBehavior.Decrement_Obeying_Policy]: 'Decrement Inventory (Obey Policies)',
};
// Wire strings live here once and are referenced by every per-entity enum.
// Snake_case values are stable and must match the pre-refactor enum so saved
// merchant column choices and existing CSV consumers keep working.
const SharedColumns = {
    customerName: 'customer_name',
    customerEmail: 'customer_email',
    createdDate: 'created_date',
    status: 'status',
    productName: 'product_name',
    productSku: 'product_sku',
    quantity: 'quantity',
    price: 'price',
};
const ClaimSharedColumns = {
    originalOrderName: 'original_order_name',
    originalOrderId: 'original_order_id',
    originalOrderDate: 'original_order_date',
    finalizedDate: 'finalized_date',
    resolutionMethod: 'resolution_method',
};
// Per-entity column keys are declared in CSV-output order. Default merchant
// columns come from `Object.values(...)`, so the listed sequence here is
// what shows up left-to-right in the exported file.
export const ReportColumnsClaim = {
    // identifiers
    claimId: 'claim_id',
    originalOrderName: ClaimSharedColumns.originalOrderName,
    originalOrderId: ClaimSharedColumns.originalOrderId,
    originalOrderDate: ClaimSharedColumns.originalOrderDate,
    // customer
    customerName: SharedColumns.customerName,
    customerEmail: SharedColumns.customerEmail,
    // claim metadata
    createdDate: SharedColumns.createdDate,
    finalizedDate: ClaimSharedColumns.finalizedDate,
    status: SharedColumns.status,
    tags: 'tags',
    // line item
    productName: SharedColumns.productName,
    productSku: SharedColumns.productSku,
    quantity: SharedColumns.quantity,
    price: SharedColumns.price,
    // line item status & resolution
    lineItemStatus: 'line_item_status',
    lineItemStatusDetail: 'line_item_status_detail',
    lineItemReason: 'line_item_reason',
    lineItemReasonDetail: 'line_item_reason_detail',
    requestedResolutionMethod: 'requested_resolution_method',
    resolutionMethod: ClaimSharedColumns.resolutionMethod,
    lineItemNoteFromCustomer: 'line_item_note_from_customer',
    lineItemNoteToCustomer: 'line_item_note_to_customer',
    // shipping & media
    returnTrackingNumber: 'return_tracking_number',
    lineItemMedia: 'line_item_media',
    // fees
    feeName: 'fee_name',
    feeApplied: 'fee_applied',
    returnShippingFeeApplied: 'return_shipping_fee_applied',
    exchangeOrderShippingFeeApplied: 'exchange_order_shipping_fee_applied',
    // inspection
    inspectionGrade: 'inspection_grade',
    inspectionDate: 'inspection_date',
    inspectionMedia: 'inspection_media',
};
export const ReportColumnsRegistration = {
    // identifiers
    registrationId: 'registration_id',
    // customer
    customerName: SharedColumns.customerName,
    customerEmail: SharedColumns.customerEmail,
    // registration metadata
    createdDate: SharedColumns.createdDate,
    status: SharedColumns.status,
    canceledDate: 'canceled_date',
    estimatedPurchaseDate: 'estimated_purchase_date',
    channel: 'channel',
    tags: 'tags',
    proofOfPurchaseFileUrls: 'proof_of_purchase_file_urls',
    // line item
    productName: SharedColumns.productName,
    productSku: SharedColumns.productSku,
    quantity: SharedColumns.quantity,
    price: SharedColumns.price,
    // marketing & incentive
    customerCreatedOnPlatform: 'customer_created_on_platform',
    emailMarketingConsentCollected: 'email_marketing_consent_collected',
    emailMarketingConsentUpdatedOnPlatform: 'email_marketing_consent_updated_on_platform',
    incentiveDiscountCode: 'incentive_discount_code',
    incentiveDiscountExpiresOn: 'incentive_discount_expires_on',
};
export const ReportColumnsShipping = {
    // identifiers
    shippingClaimId: 'shipping_claim_id',
    originalOrderName: ClaimSharedColumns.originalOrderName,
    originalOrderId: ClaimSharedColumns.originalOrderId,
    originalOrderDate: ClaimSharedColumns.originalOrderDate,
    // customer
    customerName: SharedColumns.customerName,
    customerEmail: SharedColumns.customerEmail,
    // claim metadata
    createdDate: SharedColumns.createdDate,
    finalizedDate: ClaimSharedColumns.finalizedDate,
    status: SharedColumns.status,
    reason: 'reason',
    resolutionMethod: ClaimSharedColumns.resolutionMethod,
    tags: 'tags',
    // line item
    productName: SharedColumns.productName,
    productSku: SharedColumns.productSku,
    quantity: SharedColumns.quantity,
    price: SharedColumns.price,
    // tracking
    originalOrderTrackingNumber: 'original_order_tracking_number',
    originalOrderTrackingCarrier: 'original_order_tracking_carrier',
};
/** Friendly display labels for every column value across all kinds. */
export var InventoryBehavior;
(function (InventoryBehavior) {
    InventoryBehavior["bypass"] = "bypass";
    InventoryBehavior["decrementIgnoringPolicy"] = "decrement_ignoring_policy";
    InventoryBehavior["decrementObeyingPolicy"] = "decrement_obeying_policy";
})(InventoryBehavior || (InventoryBehavior = {}));
export const ClaimPaymentStatus = {
    authorized: 'Authorized',
    captured: 'Captured',
    refunded: 'Refunded',
};
// added for display purposes just in case the enum values are ever extended and contain a non-merchant-friendly value
export const crewClaimTypeEnumName = {
    [CrewClaimTypeEnum.warranty]: 'Warranty',
    [CrewClaimTypeEnum.return]: 'Return',
};
export const crewClaimTypeEnumPluralizedName = {
    [CrewClaimTypeEnum.return]: 'Returns',
    [CrewClaimTypeEnum.warranty]: 'Warranties',
};
export var CrewIncentiveTypeEnum;
(function (CrewIncentiveTypeEnum) {
    CrewIncentiveTypeEnum["percent"] = "Percent";
    CrewIncentiveTypeEnum["fixed"] = "Fixed";
    CrewIncentiveTypeEnum["none"] = "None";
})(CrewIncentiveTypeEnum || (CrewIncentiveTypeEnum = {}));
export var CrewShippingDirectionEnum;
(function (CrewShippingDirectionEnum) {
    CrewShippingDirectionEnum["toCustomer"] = "To_Customer";
    CrewShippingDirectionEnum["fromCustomer"] = "From_Customer";
})(CrewShippingDirectionEnum || (CrewShippingDirectionEnum = {}));
// ------------------ CREW Products ------------------ //
export var StoreProductStatus;
(function (StoreProductStatus) {
    StoreProductStatus["active"] = "active";
    StoreProductStatus["draft"] = "draft";
    StoreProductStatus["archived"] = "archived";
    StoreProductStatus["unlisted"] = "unlisted";
})(StoreProductStatus || (StoreProductStatus = {}));
export const storeProductStatusName = {
    [StoreProductStatus.active]: 'Active',
    [StoreProductStatus.draft]: 'Draft',
    [StoreProductStatus.archived]: 'Archived',
    [StoreProductStatus.unlisted]: 'Unlisted',
};
// ------------------ CREW INSPECTION Classifications ------------------ //
export var InspectionGradeCode;
(function (InspectionGradeCode) {
    InspectionGradeCode["aStock"] = "A_Stock";
    InspectionGradeCode["bStock"] = "B_Stock";
    InspectionGradeCode["cStock"] = "C_Stock";
    InspectionGradeCode["dStock"] = "D_Stock";
    InspectionGradeCode["dispose"] = "Dispose";
})(InspectionGradeCode || (InspectionGradeCode = {}));
export const inspectionGradeCodeNames = {
    [InspectionGradeCode.aStock]: 'A-Stock',
    [InspectionGradeCode.bStock]: 'B-Stock',
    [InspectionGradeCode.cStock]: 'C-Stock',
    [InspectionGradeCode.dStock]: 'D-Stock',
    [InspectionGradeCode.dispose]: 'Dispose',
};
//* claim resolution method enum is used by GSP and CREW *//
export const CrewClaimResolutionMethodEnum = {
    refund: 'Refund',
    variantExchange: 'Variant_Exchange', // ? one for one of the same product
    giftCard: 'Gift_Card',
    repair: 'Repair',
    replacementOrder: 'Replacement_Order', // ? any number of products can be replaced
    warrantyReview: 'Warranty_Review',
    other: 'Other', // ? used for claims that do not have to be refunded, but still returned
};
export const CreditResolutionMethod = {
    refund: CrewClaimResolutionMethodEnum.refund,
    giftCard: CrewClaimResolutionMethodEnum.giftCard,
};
export const crewClaimResolutionMethodEnumName = {
    [CrewClaimResolutionMethodEnum.refund]: 'Refund',
    [CrewClaimResolutionMethodEnum.variantExchange]: 'Exchange',
    [CrewClaimResolutionMethodEnum.giftCard]: 'Gift Card',
    [CrewClaimResolutionMethodEnum.repair]: 'Repair',
    [CrewClaimResolutionMethodEnum.replacementOrder]: 'Replacement Order',
    [CrewClaimResolutionMethodEnum.warrantyReview]: 'Warranty Review',
    [CrewClaimResolutionMethodEnum.other]: 'Return',
};
// ------------------ CREW CLAIM REASON CATEGORY CODES ------------------ //
export const CrewClaimReasonCategoryCode = {
    defectiveItem: 'Defective_Item',
    wrongFit: 'Wrong_Fit',
    notAsExpected: 'Not_As_Expected',
    notSatisfied: 'Not_Satisfied',
    noLongerNeeded: 'No_Longer_Needed',
    wrongItem: 'Wrong_Item',
    shippingIssue: 'Shipping_Issue',
    rightToWithdraw: 'Right_To_Withdraw',
};
export const GiftCardType = {
    giftCardOrder: 'Gift_Card_Order',
    storeCredit: 'Store_Credit',
    giftCard: 'Gift_Card',
};
export const giftCardTypeName = {
    [GiftCardType.giftCardOrder]: 'Gift Card Order',
    [GiftCardType.storeCredit]: 'Store Credit',
    [GiftCardType.giftCard]: 'Gift Card',
};
export const ShipmentTrackingEventProvider = {
    corso: 'Corso',
    shopifyFulfillmentEvent: 'Shopify_Fulfillment_Event',
};
export const shipmentTrackingEventProviderName = {
    [ShipmentTrackingEventProvider.shopifyFulfillmentEvent]: 'Shopify Fulfillment Event',
    [ShipmentTrackingEventProvider.corso]: 'Corso',
};
export const crewClaimReasonCategoryCodeName = {
    [CrewClaimReasonCategoryCode.defectiveItem]: 'Defective Item',
    [CrewClaimReasonCategoryCode.wrongFit]: 'Wrong Fit',
    [CrewClaimReasonCategoryCode.notAsExpected]: 'Not As Expected',
    [CrewClaimReasonCategoryCode.notSatisfied]: 'Not Satisfied',
    [CrewClaimReasonCategoryCode.noLongerNeeded]: 'No Longer Needed',
    [CrewClaimReasonCategoryCode.wrongItem]: 'Wrong Item',
    [CrewClaimReasonCategoryCode.shippingIssue]: 'Shipping Issue',
    [CrewClaimReasonCategoryCode.rightToWithdraw]: 'Right to Withdraw',
};
// ------------------ CREW CLAIM CUSTOMER EMAIL TYPES ------------------ //
export var AutomatedEmailTypeEnum;
(function (AutomatedEmailTypeEnum) {
    // ? automated emails
    AutomatedEmailTypeEnum["returnExpiring"] = "Return_Expiring";
    AutomatedEmailTypeEnum["returnReminder"] = "Return_Reminder";
    AutomatedEmailTypeEnum["claimSubmitted"] = "Claim_Submitted";
    AutomatedEmailTypeEnum["claimShipmentCreated"] = "Claim_Shipment_Created";
    AutomatedEmailTypeEnum["registrationSubmitted"] = "Registration_Submitted";
    AutomatedEmailTypeEnum["warrantySubmitted"] = "Warranty_Submitted";
})(AutomatedEmailTypeEnum || (AutomatedEmailTypeEnum = {}));
export var FinalizationEmailTypeEnum;
(function (FinalizationEmailTypeEnum) {
    // ? finalization / processing emails
    FinalizationEmailTypeEnum["claimApprovedAsRequested"] = "Claim_Approved_As_Requested";
    FinalizationEmailTypeEnum["claimApprovedNotAsRequested"] = "Claim_Approved_Not_As_Requested";
    FinalizationEmailTypeEnum["claimDenied"] = "Claim_Denied";
    FinalizationEmailTypeEnum["claimNeedsMoreInfo"] = "Claim_Needs_More_Info";
    FinalizationEmailTypeEnum["orderGiftReturn"] = "Order_Gift_Return";
    FinalizationEmailTypeEnum["warrantyApprovedAsRequested"] = "Warranty_Approved_As_Requested";
    FinalizationEmailTypeEnum["warrantyApprovedNotAsRequested"] = "Warranty_Approved_Not_As_Requested";
    FinalizationEmailTypeEnum["warrantyDenied"] = "Warranty_Denied";
})(FinalizationEmailTypeEnum || (FinalizationEmailTypeEnum = {}));
// ------------------ CREW CLAIM RETURN SHIPMENT TYPES ------------------ //
export const ShipmentMethod = {
    packingSlip: 'Packing_Slip',
    label: 'Label',
    // TODO: workshop this type
    consolidatedDropoff: 'Consolidated_Dropoff',
};
export const shipmentMethodName = {
    [ShipmentMethod.label]: 'Shipping Label',
    [ShipmentMethod.packingSlip]: 'Packing Slip',
    [ShipmentMethod.consolidatedDropoff]: 'Happy Returns',
};
/** Warehouse systems that issue an RMA for a return shipment. */
export const RmaProvider = {
    shipBob: 'Ship_Bob',
    shipHero: 'Ship_Hero',
    blueBox: 'Blue_Box',
    xbFulfillment: 'Xb_Fulfillment',
    logiwa: 'Logiwa',
    twoBoxes: 'Two_Boxes',
    masonHub: 'Mason_Hub',
};
export const rmaProviderName = {
    [RmaProvider.shipHero]: 'ShipHero',
    [RmaProvider.shipBob]: 'ShipBob',
    [RmaProvider.blueBox]: 'BlueBox',
    [RmaProvider.xbFulfillment]: 'XB Fulfillment',
    [RmaProvider.logiwa]: 'Logiwa',
    [RmaProvider.twoBoxes]: 'Two Boxes',
    [RmaProvider.masonHub]: 'MasonHub',
};
// ------------------ CREW CLAIM STATUS CODES ------------------ //
export const CrewClaimRollupStatusCode = {
    inProgress: 'In_Progress',
    readyForReview: 'Ready_For_Review',
    completed: 'Completed',
};
export const crewClaimRollupStatusCodeName = {
    [CrewClaimRollupStatusCode.inProgress]: 'In Progress',
    [CrewClaimRollupStatusCode.readyForReview]: 'Ready For Review',
    [CrewClaimRollupStatusCode.completed]: 'Completed',
};
// ------------------ CREW CLAIM LINE ITEM STATUS CODES ------------------ //
export const CrewClaimStatusCode = {
    open: 'Open',
    approved: 'Approved',
    denied: 'Denied',
    // TODO: Rename this to "Canceled" to better match what the Shopify API uses
    closed: 'Closed',
    // remove all of these at some point
    awaitingReturn: 'Awaiting_Return',
    inTransit: 'In_Transit',
    atReturnLocation: 'At_Return_Location',
};
export const CrewClaimStatusDetailCode = {
    finalSale: 'Final_Sale',
    pendingReview: 'Pending_Review',
    pendingAdditionalInfo: 'Pending_Additional_Info',
    awaitingCustomerDropoffLabelProvided: 'Awaiting_Customer_Dropoff_Label_Provided',
    awaitingCustomerDropOffPackingSlipProvided: 'Awaiting_Customer_Dropoff_Packing_Slip_Provided',
    inTransit: 'In_Transit',
    pendingInspection: 'Pending_Inspection',
    pendingApproval: 'Pending_Approval',
    variantExchange: 'Variant_Exchange',
    giftCardIssued: 'Gift_Card_Issued',
    refunded: 'Refunded',
    refundedPartial: 'Refunded_Partial',
    gifCardIssuedPartial: 'Gift_Card_Issued_Partial',
    replacementOrder: 'Replacement_Order',
    repaired: 'Repaired',
    wrongItem: 'Wrong_Item',
    itemNotReturned: 'Item_Not_Returned',
    noncompliantReturn: 'Noncompliant_Return',
    notCoveredUnderWarranty: 'Not_Covered_Under_Warranty',
    pendingReturnShipmentCreation: 'Pending_Return_Shipment_Creation',
    returnNotRequired: 'Return_Not_Required',
    additionalInfoNotProvided: 'Additional_Info_Not_Provided',
    noActionTaken: 'No_Action_Taken',
    itemCreditAppliedToClaim: 'Item_Credit_Applied_To_Claim',
};
const claimStatusOpen = {
    code: CrewClaimStatusCode.open,
    name: 'Open',
    claimStatusDetail: [
        { code: CrewClaimStatusDetailCode.pendingReview, name: 'Pending Review' },
        {
            code: CrewClaimStatusDetailCode.pendingAdditionalInfo,
            name: 'Pending Additional Info',
        },
        {
            code: CrewClaimStatusDetailCode.pendingReturnShipmentCreation,
            name: 'Pending Return Shipment Creation',
        },
        {
            code: CrewClaimStatusDetailCode.returnNotRequired,
            name: 'Manual Review',
        },
    ],
};
const claimStatusAwaitingReturn = {
    code: CrewClaimStatusCode.awaitingReturn,
    name: 'Awaiting Return',
    claimStatusDetail: [
        {
            code: CrewClaimStatusDetailCode.awaitingCustomerDropoffLabelProvided,
            name: 'Awaiting Customer Dropoff',
        },
        {
            code: CrewClaimStatusDetailCode.awaitingCustomerDropOffPackingSlipProvided,
            name: 'Awaiting Customer Dropoff',
        },
    ],
};
const claimStatusInTransit = {
    code: CrewClaimStatusCode.inTransit,
    name: 'In Transit',
    claimStatusDetail: [
        { code: CrewClaimStatusDetailCode.inTransit, name: 'In Transit' },
    ],
};
const claimStatusAtReturnLocation = {
    code: CrewClaimStatusCode.atReturnLocation,
    name: 'At Return Location',
    claimStatusDetail: [
        {
            code: CrewClaimStatusDetailCode.pendingInspection,
            name: 'Pending Inspection',
        },
        {
            code: CrewClaimStatusDetailCode.pendingApproval,
            name: 'Pending Approval',
        },
        {
            code: CrewClaimStatusDetailCode.pendingAdditionalInfo,
            name: 'Pending Additional Info',
        },
    ],
};
const claimStatusApproved = {
    code: CrewClaimStatusCode.approved,
    name: 'Approved',
    claimStatusDetail: [
        {
            code: CrewClaimStatusDetailCode.variantExchange,
            name: 'Variant Exchange',
        },
        {
            code: CrewClaimStatusDetailCode.giftCardIssued,
            name: 'Gift Card',
        },
        {
            code: CrewClaimStatusDetailCode.refunded,
            name: 'Refund',
        },
        {
            code: CrewClaimStatusDetailCode.refundedPartial,
            name: 'Partial Refund',
        },
        {
            code: CrewClaimStatusDetailCode.gifCardIssuedPartial,
            name: 'Partial Gift Card',
        },
        {
            code: CrewClaimStatusDetailCode.replacementOrder,
            name: 'Replacement Order',
        },
        {
            code: CrewClaimStatusDetailCode.repaired,
            name: 'Repaired',
        },
        {
            code: CrewClaimStatusDetailCode.itemCreditAppliedToClaim,
            name: 'Credit Applied to Replacement Order',
        },
        {
            code: CrewClaimStatusDetailCode.noActionTaken,
            name: 'Nothing Issued Inside Corso',
        },
    ],
};
const claimStatusClosed = {
    // TODO: rename this to "Canceled" to better match what the Shopify API uses
    code: CrewClaimStatusCode.closed,
    name: 'Canceled',
    claimStatusDetail: [
        {
            code: CrewClaimStatusDetailCode.noActionTaken,
            name: 'Nothing Issued Inside Corso',
        },
    ],
};
// -------- DENIED CLAIM STATUS DETAILS -------- //
const claimStatusDenied = {
    code: CrewClaimStatusCode.denied,
    name: 'Denied',
    claimStatusDetail: [
        {
            code: CrewClaimStatusDetailCode.wrongItem,
            name: 'Wrong Item',
        },
        {
            code: CrewClaimStatusDetailCode.itemNotReturned,
            name: 'Item Not Returned',
        },
        {
            code: CrewClaimStatusDetailCode.noncompliantReturn,
            name: 'Non-compliant Return',
        },
        {
            code: CrewClaimStatusDetailCode.notCoveredUnderWarranty,
            name: 'Not Covered Under Warranty',
        },
        {
            code: CrewClaimStatusDetailCode.noActionTaken,
            name: 'Nothing Issued Inside Corso',
        },
        // ? additional info not provided was removed
    ],
};
/**
 * Helper object to assist with seeding and know which `claim_status_detail` records apply to which `claim_status`.
 * Should be instead of the underlying `claim_status_detail` related enum when working with related statuses and details.
 */
// TODO revise maybe as an object-based access of details instead of index-based access
export const crewClaimStatuses = {
    [CrewClaimStatusCode.open]: claimStatusOpen,
    [CrewClaimStatusCode.awaitingReturn]: claimStatusAwaitingReturn,
    [CrewClaimStatusCode.inTransit]: claimStatusInTransit,
    [CrewClaimStatusCode.atReturnLocation]: claimStatusAtReturnLocation,
    [CrewClaimStatusCode.approved]: claimStatusApproved,
    [CrewClaimStatusCode.denied]: claimStatusDenied,
    [CrewClaimStatusCode.closed]: claimStatusClosed,
};
export const EspMetric = {
    orderShipmentUpdate: 'Order_Shipment_Update',
    registrationCreated: 'Registration_Created',
    claimCreated: 'Claim_Created',
    claimShipmentCreated: 'Claim_Shipment_Created',
    claimShipmentInTransit: 'Claim_Shipment_In_Transit',
    claimShipmentDelivered: 'Claim_Shipment_Delivered',
    claimFinalized: 'Claim_Finalized',
    claimCancelled: 'Claim_Cancelled',
    claimOutOfStock: 'Claim_Out_Of_Stock',
};
/** The metric name a merchant sees inside Klaviyo. */
export const klaviyoEspMetricName = {
    [EspMetric.orderShipmentUpdate]: 'Corso: Order Shipment Update',
    [EspMetric.registrationCreated]: 'Corso: Registration Created',
    [EspMetric.claimCreated]: 'Corso: Claim Created',
    [EspMetric.claimShipmentCreated]: 'Corso: Claim Shipment Created',
    [EspMetric.claimShipmentInTransit]: 'Corso: Claim Shipment In Transit',
    [EspMetric.claimShipmentDelivered]: 'Corso: Claim Shipment Delivered',
    [EspMetric.claimFinalized]: 'Corso: Claim Finalized',
    [EspMetric.claimOutOfStock]: 'Corso: Claim Out Of Stock',
    [EspMetric.claimCancelled]: 'Corso: Claim Cancelled',
};
//# sourceMappingURL=crew.js.map